import React, { useState, Fragment } from 'react'
import styled, { useTheme } from 'styled-components'
import { ReactEditor, useReadOnly, useFocused, useSelected, useSlate } from 'vendor/slate-react'
import { Transforms } from 'vendor/slate'
import debounce from 'lodash-es/debounce'
import { Lightbox } from 'react-modal-image'
import AutosizeInput from 'react-input-autosize'
import { getTheme } from 'Shared/theme'

let isImageResizing = null
let isCaptionUpdating = null

const ImageWrapper = ({ attributes, children, element, editor, fileCdnPath }) => {
  const readOnly = useReadOnly()
  const { url, caption, size } = element
  const [imageSize, setImageSize] = useState(size)
  const [captionValue, setCaptionValue] = useState(caption)

  const [imageFullScreen, setImageFullScreen] = useState(false)
  const path = ReactEditor.findPath(editor, element)

  const imageUrl = fileCdnPath ? fileCdnPath + url : url
  const theme = useTheme()

  return (
    <ImageControlsWrapper {...attributes}>
      {!readOnly && (
        <ImageSizeSliderWrapper contentEditable={false}>
          <ImageSizeSlider
            type="range"
            min="10"
            max="100"
            value={imageSize || 100}
            onChange={e => {
              const { value } = e.target
              if (value.includes('application/x-slate-clipboard:')) {
                return
              }
              setImageSize(value)
              if (isImageResizing) isImageResizing.cancel()
              isImageResizing = debounce(value => {
                Transforms.setNodes(editor, { size: value }, { at: path })
              }, 300)
              isImageResizing(value)
            }}
          />
        </ImageSizeSliderWrapper>
      )}
      <ImageContentWrapper contentEditable={false}>
        {imageFullScreen && (
          <Lightbox
            medium={imageUrl}
            large={imageUrl}
            alt={captionValue}
            hideZoom={false}
            onClose={() => {
              setImageFullScreen(!imageFullScreen)
            }}
            hideDownload
          />
        )}
        <Image
          src={imageUrl}
          size={imageSize}
          imageFullScreen={imageFullScreen}
          onClick={() => {
            setImageFullScreen(!imageFullScreen)
          }}
        />
        <Caption>
          {!readOnly ? (
            <AutosizeInput
              inputStyle={{
                border: 'none',
                background: getTheme(theme.id).bgLightest,
                outline: 'none',
                boxShadow: 'none',
                fontSize: '16px',
                color: getTheme(theme.id).textGreyTinted
              }}
              placeholder="Enter a caption (optional)"
              value={captionValue || ''}
              onChange={e => {
                const { value } = e.target
                if (value.includes('application/x-slate-clipboard:')) {
                  return
                }
                setCaptionValue(value)
                if (isCaptionUpdating) isCaptionUpdating.cancel()
                isCaptionUpdating = debounce(value => {
                  Transforms.setNodes(editor, { caption: value }, { at: path })
                }, 2000)
                isCaptionUpdating(value)
              }}
            />
          ) : (
            <>{caption}</>
          )}
        </Caption>
      </ImageContentWrapper>
      {children}
    </ImageControlsWrapper>
  )
}

export default ImageWrapper

const ImageControlsWrapper = styled.div``
const ImageSizeSliderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 13px;
  align-items: center;
`

const ImageSizeSlider = styled.input`
  width: 50%;
  -webkit-appearance: none; /* Override default CSS styles */
  appearance: none;
  outline: none;
  background: ${props => props.theme.progressBarBg}; /* Grey background */
  outline: none; /* Remove outline */
  opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
  -webkit-transition: .2s; /* 0.2 seconds transition on hover */
  transition: opacity .2s;
  height:7px;
  border-radius:6px;
  align-items:center;
  margin-top:10px;
  :hover {
    opacity: 1; /* Fully shown on mouse-over */
  }
  ::-webkit-slider-thumb{
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    width: 15px; /* Set a specific slider handle width */
    height: 15px; /* Slider handle height */
    background:  ${props => props.theme.primary}; /* Green background */
    border-radius:50%;
    cursor: pointer; /* Cursor on hover */
  }
  ::-moz-range-thumb{
    width: 16px; /* Set a specific slider handle width */
    height: 16px; /* Slider handle height */
    background:  ${props => props.theme.primary}; /* Green background */
    border-radius:50%;
    cursor: pointer; /* Cursor on hover */
  }
}
`

const ImageContentWrapper = styled.div`
  width: 100%;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`

const Image = styled.img`
  display: block;
  max-width: ${props => (props.size ? `${props.size}%` : '100%')};
  min-width: ${props => (props.size ? `${props.size}%` : '100%')};
  height: 100%;
  width: ${props => (props.size ? `${props.size}%` : '100%')};
  cursor: zoom-in;
`

const Caption = styled.div`
  display: flex;
  font-size: 16px;
  justify-content: center;
  color: ${props => props.theme.textGreyTintedLighter};
  margin-top: 10px;
  margin-bottom: 10px;
  input::placeholder {
    color: ${props => props.theme.textGreyTintedLighter};
  }
`
